import { useState, useEffect } from "react";
import api from "../../../utils/api";
import { IconSpin, IconInfo, IconInterview } from "../../../utils/Icons";
import { STATUS_CFG } from "./Status";
/* ── Applicant Panel ─────────────────────────────────────────────────────────── */
function ApplicantPanel({ conv, open }) {
  const [app, setApp] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  useEffect(() => {
    if (!conv?.userId) {
      setApp(null);
      setStatus(null);
      return;
    }
    setLoading(true);
    api
      .get("/applications")
      .then(({ data }) => {
        const all = data.filter(
          (a) =>
            a.userId === conv.userId ||
            a.userId?.toString() === conv.userId?.toString(),
        );
        const candidate =
          all.find((a) => a.status === "pending" || a.status === "reviewed") ||
          all[0];
        if (candidate) {
          setApp(candidate);
          setStatus(candidate.status);
        } else {
          setApp(null);
          setStatus(null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [conv?.userId]);

  const updateStatus = async (newStatus) => {
    if (!app || saving || newStatus === status) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/applications/${app._id}/status`, {
        status: newStatus,
      });
      setStatus(data.status);
      setApp((prev) => ({ ...prev, status: data.status }));
      showToast(`Status updated to ${data.status}`);
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to update status",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const sendInterview = async () => {
    if (!app || sendingInvite) return;
    setSendingInvite(true);
    try {
      await api.post(`/applications/${app._id}/interview`);
      showToast("Interview invitation sent via chat & email!");
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to send invite",
        "error",
      );
    } finally {
      setSendingInvite(false);
    }
  };

  return (
    <>
      {toast && (
        <div className={`achat-toast ${toast.type}`}>
          {toast.type === "success" ? (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <IconInfo />
          )}
          {toast.msg}
        </div>
      )}

      <div className={`achat-app-panel${open ? " open" : ""}`}>
        {open && (
          <div className="achat-app-panel-inner">
            {loading ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "var(--text-3)",
                  fontSize: ".78rem",
                }}
              >
                <IconSpin size={13} /> Loading application…
              </div>
            ) : !app ? (
              <div style={{ fontSize: ".78rem", color: "var(--text-3)" }}>
                No open application found for this user.
              </div>
            ) : (
              <>
                <div className="achat-app-info-grid">
                  <div className="achat-app-info-item">
                    <div className="achat-app-info-label">Position</div>
                    <div className="achat-app-info-val">
                      {app.jobTitle || "—"}
                    </div>
                  </div>
                  <div className="achat-app-info-item">
                    <div className="achat-app-info-label">Experience</div>
                    <div className="achat-app-info-val">
                      {app.experience || "—"}
                    </div>
                  </div>
                  <div className="achat-app-info-item">
                    <div className="achat-app-info-label">Phone</div>
                    <div className="achat-app-info-val">{app.phone || "—"}</div>
                  </div>
                  <div className="achat-app-info-item">
                    <div className="achat-app-info-label">Applied</div>
                    <div className="achat-app-info-val">
                      {app.appliedAt
                        ? new Date(app.appliedAt).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </div>
                  </div>
                </div>

                <div className="achat-status-row">
                  <span className="achat-status-label">Status:</span>
                  {["pending", "reviewed", "accepted", "rejected"].map((s) => (
                    <button
                      key={s}
                      className={`achat-status-btn ${s}${status === s ? " active" : ""}`}
                      onClick={() => updateStatus(s)}
                      disabled={saving || status === s}
                    >
                      {saving && status !== s ? "…" : STATUS_CFG[s].label}
                    </button>
                  ))}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  {app.linkedin && (
                    <a
                      href={app.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: ".73rem",
                        color: "var(--blue)",
                        textDecoration: "none",
                      }}
                    >
                      LinkedIn ↗
                    </a>
                  )}
                  {app.portfolio && (
                    <a
                      href={app.portfolio}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: ".73rem",
                        color: "var(--blue)",
                        textDecoration: "none",
                      }}
                    >
                      Portfolio ↗
                    </a>
                  )}
                  <button
                    className="achat-interview-btn"
                    onClick={sendInterview}
                    disabled={sendingInvite || app.interviewInviteSent}
                    title={
                      app.interviewInviteSent
                        ? "Invite already sent"
                        : "Send interview invitation"
                    }
                  >
                    {sendingInvite ? <IconSpin size={11} /> : <IconInterview />}
                    {app.interviewInviteSent ? "Invite Sent" : "Send Invite"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default ApplicantPanel;
